import { useState } from "react";
import type { FC, ReactNode } from "react";
import { Outlet } from "react-router-dom";
import { experimentalStyled } from "@mui/material";
import DashboardNavbar from "./DashboardNavbar";
import DashboardSidebar from "./DashboardSidebar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

interface DashboardLayoutProps {
	children?: ReactNode;
}

const DashboardLayoutRoot = experimentalStyled("div")(({ theme }) => ({
	backgroundColor: theme.palette.background.paper,
	display: "flex",
	height: "100%",
	overflow: "hidden",
	width: "100%",
}));

const DashboardLayoutWrapper = experimentalStyled("div")(({ theme }) => ({
	display: "flex",
	flex: "1 1 auto",
	overflow: "hidden",
	height: "100%",
	[theme.breakpoints.up("lg")]: {
		paddingLeft: "320px",
	},
}));

const DashboardLayoutContainer = experimentalStyled("div")({
	display: "flex",
	flex: "1 1 auto",
	overflow: "hidden",
	height: "100%",
});

const DashboardLayoutContent = experimentalStyled("div")({
	flex: "1 1 auto",
	height: "100%",
	overflow: "auto",
	position: "relative",
	WebkitOverflowScrolling: "touch",
});

const DashboardLayout: FC<DashboardLayoutProps> = () => {
	const [isSidebarMobileOpen, setIsSidebarMobileOpen] =
		useState<boolean>(false);

	return (
		<DashboardLayoutRoot data-testid="dashboard-layout-root">
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DashboardNavbar
					onSidebarMobileOpen={(): void =>
						setIsSidebarMobileOpen(true)
					}
				/>
				<DashboardSidebar
					onMobileClose={(): void => setIsSidebarMobileOpen(false)}
					openMobile={isSidebarMobileOpen}
				/>
				<DashboardLayoutWrapper data-testid="dashboard-content-wrapper">
					<DashboardLayoutContainer>
						<DashboardLayoutContent data-testid="dashboard-main-content">
							<Outlet />
						</DashboardLayoutContent>
					</DashboardLayoutContainer>
				</DashboardLayoutWrapper>
			</LocalizationProvider>
		</DashboardLayoutRoot>
	);
};

export default DashboardLayout;
